import React, { cloneElement, useEffect, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextInput } from '../common/text-input';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';

interface ModalTagCreateProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  loading?: boolean;
  onConfirm?: (name: string) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 标签创建弹窗 */
export const ModalTagCreate: React.FC<ModalTagCreateProps> = ({
  trigger,
  open: externalOpen,
  title,
  loading,
  onConfirm,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [name, setName] = useState('');

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (open !== currentOpen) {
      setName('');
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };
  
  useEffect(() => {
    return () => {
      setName('');
    }
  }, []);

  const handleConfirm = async () => {
    if (!name || loading) return;
    try {
      await onConfirm?.(name);
      handleClose();
    } catch (error) {
      console.error("Failed to create tag:", error);
      throw error;
    }
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-[1003]")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[400px] max-h-[229px] min-h-[229px] flex-shrink-0",
          "z-[1003] overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4">
          <Dialog.Title
            className='focus:outline-none text-[rgba(16,24,40,1)] text-base font-medium flex items-center'
          >
            <span>{title || t('assets.material_item.tags.create')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              icon={<X className="size-6 text-[rgba(71,84,103,1)]" />}
              onClick={handleClose}

            />
          </Dialog.Close>
        </div>
        <div className='flex flex-col px-6 pt-0 pb-6 select-none shrink-0'>
          <div className='flex flex-col gap-2'>
            <TextInput
              type="text"
              value={name}
              placeholder={t('assets.material_item.tags.tag_name_placeholder')}
              className='h-11 bg-[rgba(242,244,247,1)] border-none focus:outline-none font-medium'
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              onKeyDown={async(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await handleConfirm();
                }
              }}
            />
            <div className='flex justify-between items-center text-[rgba(102,112,133,1)] text-xs'>
              <span>{t('assets.material_item.tags.tips')}</span>
              <span>{name.length}/20</span>
            </div>
          </div>
        </div>
        <div className={cn("flex gap-3 px-6 pb-6 mt-auto shrink-0")}>
          <button
            onClick={() => handleOpenChange(false)}
            className={cn(
              "w-[170px] max-h-11 px-4 py-2.5",
              "text-base text-i-black font-medium",
              "border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-50 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-200"
            )}
          >
            {t('assets.material_item.tags.cancel')}
          </button>
          <TextButton
            label={t('assets.material_item.tags.confirm')}
            onClick={handleConfirm}
            loading={loading}
            disabled={loading || !name}
            className={cn(
              "w-[170px] h-11 px-4",
              "text-base text-i-white font-medium",
              "bg-i-normal rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-800 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-400",
            )}
          >
          </TextButton>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}